import { useContext } from 'react';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { useTheme } from '@startuptools/ui/Theme';
import { Typography } from '@startuptools/ui/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { isNil } from 'lodash-es';
import { DownloadedContext } from '../contexts/DownloadedContext';
import { type GqlUser } from '../../graphql/base-types.graphql';

export const Header = ({ user }: { user: Pick<GqlUser, 'id'> | null | undefined }) => {
  const { downloadedState } = useContext(DownloadedContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return isMobile ? (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={{
        width: '100%',
        height: 'auto',
        marginTop: '0rem',
        overflow: 'hidden',
      }}
    >
      <Box
        display="flex"
        justifyContent="center"
        alignItems="start"
        height="auto"
        flex={1}
        width="100%"
        overflow="hidden"
        sx={theme => ({
          backgroundImage: "url('./assets/images/free_templates/graphical_t.svg')",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'auto',
          backgroundColor: theme.palette.pro.background,
        })}
      >
        <Box
          component="img"
          src=".assets/images/free_templates/office_picture.png"
          alt="People sitting in an office"
          sx={{
            width: 'auto',
            height: 'auto',
            marginTop: '1.375rem',
            marginBottom: '1.375rem',
          }}
        />
      </Box>

      <Box flex={1} display="flex" justifyContent="center" alignItems="start" height="100%">
        <Box display="flex" alignContent="center" flexDirection="column" width="90%" marginTop="1.375rem">
          <Typography
            level="h3"
            sx={{
              width: '100%',
              height: 'auto',
              fontFamily: 'Inter',
              fontStyle: 'normal',
              fontWeight: 700,
              fontSize: '2.5rem',
              lineHeight: '3rem',
              background: `linear-gradient(301.28deg, ${theme.palette.primary[500]} 36.76%, ${theme.palette.primary[800]} 157.58%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              marginBottom: '2rem',
            }}
          >
            {downloadedState
              ? 'Thank you very much for downloading!'
              : !isNil(user)
                ? 'Get the documents and support you need!'
                : 'Welcome to our thriving community'}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Inter',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '1rem',
              lineHeight: '1.5rem',
              width: '100%',
              height: '4.5rem',
              marginBottom: '3rem',
            }}
          >
            {isNil(user)
              ? [
                  'We are committed to support your entrepreneurial experience. In addition to our high-quality templates, our platform offers a comprehensive document management solution.',
                  'Seamlessly fill out your documents, conveniently obtain electronic signatures, and securely store your files.',
                  'We are dedicated to empowering entrepreneurs with efficient tools to build a better future.',
                ]
              : [
                  'As a valued user, we are committed to enhance your entrepreneurial experience.',
                  'Seamlessly fill out your documents, conveniently obtain electronic signatures, and securely store your files.',
                  'We are dedicated to empowering you and your allies with efficient tools to build a better future.',
                ]}
          </Typography>
          {isNil(user) && (
            <a href="/" style={{ textDecoration: 'none' }}>
              <Button sx={{ width: '100%', marginTop: '10rem' }}>{'Sign up for free with BankID'}</Button>
            </a>
          )}
        </Box>
      </Box>
    </Box>
  ) : (
    <Box
      display="flex"
      sx={{
        height: '38.3125rem',
        backgroundColor: theme.palette.pro.background,
        marginTop: '0rem',
        overflow: 'visible',
        backgroundImage: `url('./assets/images/free_templates/graphical_t.svg')`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: {
          md: '41rem -8rem',
          lg: '44rem -8rem',
          xl: '49rem -8rem',
        },
      }}
    >
      <Box
        flex={1}
        display="flex"
        justifyContent="start"
        alignItems="start"
        height="100%"
        sx={{
          marginLeft: {
            md: '5rem',
            lg: '9.875rem',
            xl: '12rem',
          },
        }}
      >
        <Box display="flex" flexDirection="column" width="33.6875rem" marginTop="7.5625rem">
          <Typography
            level="h3"
            sx={{
              width: '33.6875rem',
              height: '6rem',
              fontFamily: 'Inter',
              fontStyle: 'normal',
              fontWeight: 700,
              fontSize: '2.5rem',
              lineHeight: '3rem',
              background: `linear-gradient(301.28deg, ${theme.palette.primary[500]} 36.76%, ${theme.palette.primary[800]} 157.58%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              marginBottom: '1rem',
            }}
          >
            {downloadedState
              ? 'Thank you very much for downloading!'
              : !isNil(user)
                ? 'Get the documents and support you need!'
                : 'Welcome to our thriving community'}
          </Typography>
          <Box
            sx={{
              fontFamily: 'Inter',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '1rem',
              lineHeight: '1.5rem',
              width: '33.1875rem',
              height: '4.5rem',
              marginBottom: '7.5rem',
            }}
          >
            {isNil(user)
              ? [
                  'We are committed to support your entrepreneurial experience. In addition to our high-quality templates, our platform offers a comprehensive document management solution.',
                  'Seamlessly fill out your documents, conveniently obtain electronic signatures, and securely store your files.',
                  'We are dedicated to empowering entrepreneurs with efficient tools to build a better future.',
                ].map(paragraph => (
                  <Typography key={paragraph} sx={{ mb: '0.75rem' }}>
                    {paragraph}
                  </Typography>
                ))
              : downloadedState
                ? [
                    'Congratulations on taking a significant step towards building a better future for your venture.',
                    "Whether you're starting a new business or scaling an existing one, our high-quality templates are designed to streamline your processes and drive success",
                    "We're excited to be a part of your entrepreneurial growth and look forward to supporting you on this exciting path.",
                  ].map(paragraph => (
                    <Typography key={paragraph} sx={{ mb: '0.75rem' }}>
                      {paragraph}
                    </Typography>
                  ))
                : [
                    'As a valued user, we are committed to enhance your entrepreneurial experience.',
                    'Seamlessly fill out your documents, conveniently obtain electronic signatures, and securely store your files.',
                    'We are dedicated to empowering you and your allies with efficient tools to build a better future.',
                  ].map(paragraph => (
                    <Typography key={paragraph} sx={{ mb: '0.75rem' }}>
                      {paragraph}
                    </Typography>
                  ))}
          </Box>
          {isNil(user) && (
            <a href="/" style={{ textDecoration: 'none' }}>
              <Button sx={{ marginTop: '1rem' }}>{'Sign up for free with BankID'}</Button>
            </a>
          )}
        </Box>
      </Box>
      <Box flex={1} height="100%" overflow={'visible'}>
        <Box display="flex" justifyContent="center" alignItems="start" height="100%">
          <Box
            component="img"
            src="./assets/images/free_templates/office_picture.png"
            alt="People sitting in an office"
            sx={{
              width: {
                md: '23rem',
                lg: '30rem',
                xl: '32.125rem',
              },
              height: 'auto',
              marginTop: {
                md: '6rem',
                lg: '7rem',
                xl: '6.6875rem',
              },
              overflow: 'hidden',
              marginRight: {
                md: '9rem',
                lg: '15rem',
                xl: '17.3125rem',
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
